import { useState } from "react";
import { TellescopeModelFromName, useTellescopeInfiniteQuery, useTellescopeMutation } from "@tellescope/tanstack-query";
import { ConfirmDialog, DashboardCard } from "@tellescope/ui";
import { formatDateTime } from "@tellescope/utils";
import { useTranslation } from "react-i18next";

export interface OrdersSectionProps {
  contact: TellescopeModelFromName<"endusers">;
}

/**
 * Maps order status to badge variant for visual styling
 */
function getStatusVariant(status: string): "default" | "secondary" | "destructive" {
  const normalized = status.toLowerCase();
  if (normalized.includes("deliver") || normalized.includes("complete")) return "default";
  if (normalized.includes("ship") || normalized.includes("transit")) return "default";
  if (normalized.includes("pending") || normalized.includes("process")) return "secondary";
  if (normalized.includes("error") || normalized.includes("cancel") || normalized.includes("fail"))
    return "destructive";
  return "secondary";
}

export const OrdersSection = ({ contact }: OrdersSectionProps) => {
  const { t } = useTranslation("device-orders");
  const [orderToDelete, setOrderToDelete] = useState<string | null>(null);

  const ordersQuery = useTellescopeInfiniteQuery({
    model: "enduser_orders",
    method: "getSome",
    request: {
      filter: {
        enduserId: contact.id,
      },
    },
    select: (data) => data.pages.flat(),
  });

  const deleteOrderMutation = useTellescopeMutation({
    model: "enduser_orders",
    method: "deleteOne",

    onSuccess: () => {
      setOrderToDelete(null);
    },
  });

  if (ordersQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("Loading orders...")}</div>
        </div>
      </div>
    );
  }

  if (!ordersQuery.data?.length) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("No orders found")}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto space-y-4 pr-2">
        {ordersQuery.data.map((order) => (
          <DashboardCard
            key={order.id}
            statusLabel={order.status}
            status={getStatusVariant(order.status)}
            title={order.title}
            subtitle={formatDateTime(order.updatedAt, "MM/DD/YYYY at hh:mm a", true)}
            assignees={[]}
            onDelete={() => setOrderToDelete(order.id)}
            deletePosition="status"
            className="w-full"
          />
        ))}
      </div>

      <ConfirmDialog
        open={!!orderToDelete}
        onOpenChange={(open) => !open && setOrderToDelete(null)}
        variant="destructive"
        title={t("Delete Order")}
        description={t("Are you sure you want to delete this order? This action cannot be undone.")}
        confirmText={t("Delete")}
        onConfirm={async () => {
          if (orderToDelete) {
            await deleteOrderMutation.mutateAsync(orderToDelete);
          }
        }}
      />
    </>
  );
};
